// Lightweight logger utility used across the app
// Exposes structured logging helpers with optional context and error objects

export type LogContext = Record<string, unknown> | undefined;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

function shouldLog(level: LogLevel): boolean {
	// Default behavior: log everything in development, suppress debug in production
	const nodeEnv = typeof process !== 'undefined' ? process.env.NODE_ENV : 'production';
	if (level === 'debug' && nodeEnv === 'production') return false;
	return true;
}

function format(level: LogLevel, message: string, context?: LogContext): string {
	const time = new Date().toISOString();
	const ctx = context ? ` | context: ${JSON.stringify(context)}` : '';
	return `[${time}] ${level.toUpperCase()}: ${message}${ctx}`;
}

function logError(message: string, context?: LogContext, error?: Error): void {
	if (!shouldLog('error')) return;
	const base = format('error', message, context);
	if (error) {
		// Preserve original error for stack visibility
		// eslint-disable-next-line no-console
		console.error(base, error);
	} else {
		// eslint-disable-next-line no-console
		console.error(base);
	}
}

function logInfo(message: string, context?: LogContext): void {
	if (!shouldLog('info')) return;
	// eslint-disable-next-line no-console
	console.info(format('info', message, context));
}

function logWarn(message: string, context?: LogContext): void {
	if (!shouldLog('warn')) return;
	// eslint-disable-next-line no-console
	console.warn(format('warn', message, context));
}

function logDebug(message: string, context?: LogContext): void {
	if (!shouldLog('debug')) return;
	// eslint-disable-next-line no-console
	console.debug(format('debug', message, context));
}

export const logger = {
	info: logInfo,
	warn: logWarn,
	debug: logDebug,
	error: logError,
};

export default logger;



